import {
    Button,
    Divider,
    ListItemText,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';
import {
    findMatchingPreset,
    formatPresetLabel,
    WindowLevel,
} from '~/domain/patient/windowLevel';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import { WindowLevelSource } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { joinedButtonSx, TRIGGER_CHEVRON_SIZE_PX } from '../chrome';
import ConsoleMenu from '../ConsoleMenu';
import MenuCheck from '~/components/Menu/MenuCheck';

interface WindowPresetMenuProps {
    /** Per-series window/level truth; supplies the presets and the active one. */
    readonly source: WindowLevelSource;
    /**
     * Applies the preset of that name, from the list the source gives, to
     * every series it reaches.
     */
    readonly onSelectPreset: (presetName: string) => void;
    /** Sets the series' own window, and no other series'. */
    readonly onSetWindowLevel: (windowLevel: WindowLevel) => void;
    /**
     * Names the image the series is, without PHI, e.g. its modality; the
     * control is named after it, so two of them never read alike.
     */
    readonly imageLabel?: string;
}

/**
 * The list of named windows for one series, opened from a framed chevron
 * joined to the right edge of the frame around the values it changes.
 *
 * The values already say what the window is, so the trigger shows no
 * preset name of its own. The name of the window in force is in the
 * trigger's accessible name and tooltip, and the list checks it. The list
 * opens upward over the values, so its last entry sits next to the
 * trigger: Reset, which returns this series alone to the window the image
 * gives for a reset, so there is always a way back that leaves every other
 * series as it is; before the series loads it is refused and says why.
 * Above it the named windows, led by the window the series arrived with.
 *
 * A preset the series cannot supply stays listed and refused, so the user can
 * see that the device knows of it.
 */
export default function WindowPresetMenu({
    source,
    onSelectPreset,
    onSetWindowLevel,
    imageLabel,
}: WindowPresetMenuProps) {
    const intl = useIntl();
    const menu = useMenuAnchor();
    const resetText = intl.formatMessage({ id: 'windowPreset.reset' });
    useChangeVersion(source);
    // Read per render, which this bar does at the rate a window/level drag
    // publishes. The measured windows behind the list are derived once and
    // cached on the image, so what repeats here is only the list itself.
    const presets = source.getPresets();
    const windowLevel = source.getWindowLevel();
    const canReset = source.getResetWindowLevel() !== undefined;
    const active = windowLevel
        ? findMatchingPreset(presets, windowLevel.window, windowLevel.level)
        : undefined;
    const controlName = intl.formatMessage(
        { id: 'windowPreset.withPreset' },
        {
            control: intl.formatMessage({ id: 'windowPreset.name' }),
            preset: active?.name ?? intl.formatMessage({ id: 'picker.custom' }),
        }
    );
    const triggerName = imageLabel
        ? intl.formatMessage(
              { id: 'windowPreset.ofImage' },
              { image: imageLabel, control: controlName }
          )
        : controlName;

    return (
        <>
            <Tooltip title={triggerName} placement="top">
                <Button
                    variant="picker"
                    aria-label={triggerName}
                    aria-haspopup="menu"
                    aria-expanded={menu.open}
                    data-testid="window-preset-trigger"
                    onClick={(event: ReactMouseEvent<HTMLButtonElement>) =>
                        menu.onOpen(event.currentTarget)
                    }
                    endIcon={<ChevronDown size={TRIGGER_CHEVRON_SIZE_PX} />}
                    sx={{
                        ...joinedButtonSx('left'),
                        px: 1,
                        // The chevron stands alone, so it sits centred.
                        '& .MuiButton-endIcon': { ml: 0, mr: 0 },
                    }}
                />
            </Tooltip>
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
                minWidth={232}
                // Grows leftward, over the values it changes.
                align="right"
            >
                {presets.map((preset) => {
                    const isActive = preset.name === active?.name;
                    return (
                        <MenuItem
                            key={preset.name}
                            dense
                            disabled={preset.disabled}
                            selected={isActive}
                            onClick={() => {
                                // A disabled item still receives a click from
                                // assistive technology and from a pointer that
                                // ignores the CSS: refuse it here, so a window
                                // the series does not have can never be applied.
                                if (preset.disabled) {
                                    return;
                                }
                                onSelectPreset(preset.name);
                                menu.onClose();
                            }}
                        >
                            <ListItemText>
                                {formatPresetLabel(preset)}
                            </ListItemText>
                            <MenuCheck selected={isActive} />
                        </MenuItem>
                    );
                })}
                <Divider />
                <MenuItem
                    dense
                    data-testid="window-preset-reset"
                    disabled={!canReset}
                    onClick={() => {
                        // Read at the click: the image's reset window
                        // changes as its load completes, and that change
                        // publishes no window event to re-render this list.
                        const target = source.getResetWindowLevel();
                        if (!target) {
                            return;
                        }
                        onSetWindowLevel(target);
                        menu.onClose();
                    }}
                >
                    <ListItemText
                        primary={resetText}
                        secondary={
                            canReset
                                ? undefined
                                : intl.formatMessage({
                                      id: 'tool.disabled.noImage',
                                  })
                        }
                    />
                </MenuItem>
            </ConsoleMenu>
        </>
    );
}
